import { Vue } from 'vue-class-component';
import {
  ActionDTO,
  ConditionDTO,
  SceneObjectType,
  TriggerDTO,
} from '@/../client-generator/generated-clients/plants';
import { SceneUtils } from '@/core/utils/scene.utils';

export enum SceneSlideType {
  ACTION,
  TRIGGER,
  CONDITION,
}

export abstract class BaseSceneSlide extends Vue {
  SceneUtils = SceneUtils;
  slideType!: SceneSlideType;
  items: ActionDTO[] | TriggerDTO[] | ConditionDTO[] = [];

  SLIDE_WIDTH = 0;
  SLIDE_SHIFT_PX = 0;
  SLIDE_WRAPPER_WIDTH = 0;
  readonly SLIDE_SHIFT_MARGIN = 30;

  currentStep = 1;
  currentNegativeLXMargin = 0;

  // display properties
  showHandlers = false;

  mounted() {
    this.attachListeners();
  }

  unmounted() {
    this.detachListeners();
  }

  get slideTitle(): string {
    switch (this.slideType) {
      case SceneSlideType.ACTION:
        return this.$t('AppResource.ActionsDo');
      case SceneSlideType.TRIGGER:
        return this.$t('AppResource.StartTriggersWhen');
      case SceneSlideType.CONDITION:
        return this.$t('AppResource.ConditionsIf');
      default:
        return '';
    }
  }

  get isActionSlide(): boolean {
    return this.slideType === SceneSlideType.ACTION;
  }

  get isConditionSlide(): boolean {
    return this.slideType === SceneSlideType.CONDITION;
  }

  get isTriggerSlide(): boolean {
    return this.slideType === SceneSlideType.TRIGGER;
  }

  get timeBasedObjectDescriptor(): number {
    return SceneObjectType.DateTime;
  }

  get totalSteps(): number {
    return this.items.length;
  }

  get isLastStep(): boolean {
    return this.currentStep === this.totalSteps;
  }

  get isFirstStep(): boolean {
    return this.currentStep === 1;
  }

  private getFirstActualSlide(): HTMLDivElement {
    return [...this.$el.children].find(
      (e: HTMLDivElement, i) =>
        !e.classList.contains('room-object--start') && i !== 0
    )!;
  }

  prevSlide() {
    this.setSizes();
    if (this.isFirstStep) return;
    this.currentNegativeLXMargin += this.SLIDE_SHIFT_PX;
    this.getFirstActualSlide().style.marginLeft = `${
      this.currentNegativeLXMargin + this.SLIDE_SHIFT_MARGIN
    }px`;
    this.currentStep--;
  }

  nextSlide() {
    this.setSizes();
    if (this.isLastStep) return;
    this.currentNegativeLXMargin -= this.SLIDE_SHIFT_PX;
    this.getFirstActualSlide().style.marginLeft = `${this.currentNegativeLXMargin}px`;
    this.currentStep++;

    if (!this.isFirstStep) {
      this.getFirstActualSlide().style.marginLeft = `${
        this.currentNegativeLXMargin + this.SLIDE_SHIFT_MARGIN
      }px`;
    }
  }

  protected inferSlideType() {
    this.setSizes();
    this.showHandlersIfNeeded();
  }

  private onWindowResize() {
    this.setSizes();
  }

  private attachListeners() {
    window.addEventListener('resize', this.onWindowResize);
  }

  private detachListeners() {
    window.removeEventListener('resize', this.onWindowResize);
  }

  private setSizes(): void {
    this.SLIDE_WIDTH = this.getFirstActualSlide().offsetWidth;
    this.SLIDE_SHIFT_PX = this.SLIDE_WIDTH + this.SLIDE_SHIFT_MARGIN;
    this.SLIDE_WRAPPER_WIDTH = (this.$refs
      .slidesWrp as HTMLDivElement).offsetWidth;
  }

  private showHandlersIfNeeded() {
    // +1 takes in account the static slide
    // which is inside the container too
    this.showHandlers =
      (this.items.length + 1) * this.SLIDE_SHIFT_PX > this.SLIDE_WRAPPER_WIDTH;
  }
}
